import type { ReactNode } from 'react';
import type { To } from 'react-router';
import React from 'react';

import type { SizingScale } from '../../utilities/shared/sizes';
import type { PortOrientation, PortPort, PortProps } from '../Port/Port';
import type { PortSpeed } from '../Port/utils';
import type { SlotOrientation, SlotProps } from './Slots';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { truncateMiddle } from '../../utilities/strings';
import { LEDs } from '../LEDs/LEDs';
import { Port } from '../Port/Port';
import { isGen1Hardware } from '../utils';
import { Slot } from './Slots';

// Keep in sync with `DeviceModel` in common/src/gql/graphql.ts
export enum HardwareDeviceModel {
  A1 = 'A1',
  A2 = 'A2',
  F1 = 'F1',
  F2 = 'F2',
  F3 = 'F3',
  G1 = 'G1',
  Mc01 = 'MC01',
  Mc05 = 'MC05',
  Mc06 = 'MC06',
  Mc11 = 'MC11',
  Mp01 = 'MP01',
  Ms07 = 'MS07',
  Ms10 = 'MS10',
  Ms11 = 'MS11',
  Ms12 = 'MS12',
  Mw02 = 'MW02',
  Mw03 = 'MW03',
  Mw04 = 'MW04',
  Mw05 = 'MW05',
  Mw06 = 'MW06',
  Mw07 = 'MW07',
  Mw08 = 'MW08',
  Mw09 = 'MW09',
  P1 = 'P1',
  Psgs1518Af = 'PSGS1518AF',
  Psgs1526F = 'PSGS1526F',
  Psgs1626Gf = 'PSGS1626GF',
  Psgs2326Kf = 'PSGS2326KF',
  Psgs2852Bf = 'PSGS2852BF',
  Psgs2852Xbf = 'PSGS2852XBF',
  Psgs2852Xbl = 'PSGS2852XBL',
  S1 = 'S1',
  S2 = 'S2',
  S3 = 'S3',
}

const HardwareSlotsRear = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  padding: '$16',
  margin: '-$16 -$16 -$16 $16',
  background: colors.bgNeutralLight,
  strokeLeft: colors.strokeNeutralLight,
  borderRadius: '0 $12 $12 0',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
    strokeLeft: colors.strokeNeutralDark,
  },
});
HardwareSlotsRear.displayName = 'HardwareSlotsRear';

const HardwareSlotsRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$8',
  height: '100%',
});
HardwareSlotsRow.displayName = 'HardwareSlotsRow';

const HardwareSlotsColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  position: 'relative',
});
HardwareSlotsColumn.displayName = 'HardwareSlotsColumn';

const HardwareSlotsTrailingGap = styled('div', {
  display: 'flex',
  height: '100%',
  position: 'relative',
});
HardwareSlotsTrailingGap.displayName = 'HardwareSlotsTrailingGap';

export const getPortProps = (ports: PortProps[], portNumber: number): PortProps | undefined => {
  const port = ports.find((p) => p.number === portNumber);
  if (port) {
    // Remove onClick when Port is inside a Slot to avoid double focus
    const { onClick, ...portPropsWithoutOnClick } = port;
    return portPropsWithoutOnClick;
  }
  return undefined;
};

export const getSlotProps = (ports: PortProps[], portNumber: number): SlotProps | undefined => {
  const portProps = ports.find((p) => p.number === portNumber);
  if (portProps) {
    const { orientation, ...restPortProps } = portProps;
    return {
      ...restPortProps,
      connection: portProps.connection,
      onClick: portProps.onClick,
      diagram: <div>{portProps.number}</div>,
    };
  }
  return undefined;
};

export interface PortRow {
  ports: number[];
  orientation: SlotOrientation;
  portOrientation: PortOrientation;
  portSpeed?: PortSpeed;
}

export interface PortColumn {
  type: PortPort;
  rows: PortRow[];
  trailingGap?: SizingScale;
  group?: string;
}

export interface HardwareSlotsLayout {
  columns: PortColumn[];
  rearColumns?: PortColumn[];
  portLabels?: Record<number, string>;
}

export const renderHardwarePortRow = (
  key: string,
  ports: PortProps[],
  portNumbers: number[],
  orientation: SlotOrientation,
  portType: PortPort,
  portOrientation: PortOrientation,
  portLabels?: Record<number, string>,
) => (
  <HardwareSlotsRow key={key}>
    {portNumbers.map((portNumber) => {
      const portProps = getPortProps(ports, portNumber);
      const isDisabled = portProps?.status === 'disabled';

      const port = (
        <Port {...portProps} number={portNumber} port={portType} orientation={portOrientation} />
      );

      return (
        <Slot
          key={`${key}-port-${portNumber}`}
          {...getSlotProps(ports, portNumber)}
          number={portNumber}
          label={portLabels?.[portNumber]}
          orientation={orientation}
          diagram={
            isDisabled ? (
              <Tooltip contents={portProps?.status === 'disabled' ? 'Disabled' : null}>
                {port}
              </Tooltip>
            ) : (
              port
            )
          }
        />
      );
    })}
  </HardwareSlotsRow>
);

export interface HardwareSlotsLayoutRendererProps {
  label: {
    as?: React.ElementType;
    to?: To;
    content: ReactNode;
  };
  layout: HardwareSlotsLayout;
  model: HardwareDeviceModel;
  ports: PortProps[];
}

const SlotsLabel = styled(Small, {
  heightAll: '100%',
  padding: '$4 $2',
  background: colors.bgNeutralLight,
  borderRadius: '$6',
  transform: 'rotate(180deg)',
  writingMode: 'vertical-lr',
  fontWeight: '$bold',
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
  },
});
SlotsLabel.displayName = 'SlotsLabel';

const SlotsMetadata = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  gap: '$8',
});
SlotsMetadata.displayName = 'SlotsMetadata';

const GroupLabel = styled('div', {
  position: 'absolute',
  top: '-$12',
  left: '-$4',
  right: '-$4',
  display: 'flex',
  alignItems: 'center',
  gap: '$4',
});
GroupLabel.displayName = 'GroupLabel';

const GroupLine = styled('div', {
  flex: 1,
  height: '1px',
  background: colors.strokeNeutralLight,
  position: 'relative',

  [darkThemeSelector]: {
    background: colors.strokeNeutralDark,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    width: '1px',
    height: '$6',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },

  variants: {
    side: {
      left: {
        '&::after': {
          left: 0,
        },
      },
      right: {
        '&::after': {
          right: 0,
        },
      },
    },
  },
});
GroupLine.displayName = 'GroupLine';

const GroupText = styled('span', {
  whiteSpace: 'nowrap',
  color: colors.gray500,
  fontSize: '$10',
  lineHeight: 1,
});
GroupText.displayName = 'GroupText';

export function HardwareSlotsLayoutRenderer({
  label,
  layout,
  model,
  ports,
}: HardwareSlotsLayoutRendererProps) {
  const gen1Hardware = isGen1Hardware(model);
  const hasMetadata = label || gen1Hardware;

  return (
    <HardwareSlotsRow>
      {hasMetadata && (
        <SlotsMetadata>
          {label && (
            <SlotsLabel
              as={label.as}
              to={label.to}
              title={typeof label.content === 'string' ? label.content : undefined}
              family="monospace"
              weight="bold"
            >
              {typeof label.content === 'string'
                ? truncateMiddle(label.content, 13)
                : label.content}
            </SlotsLabel>
          )}
          {gen1Hardware && <LEDs state="configured" />}
        </SlotsMetadata>
      )}
      {layout.columns.map((column, columnIndex) => {
        const columnKey = `column-${label.content}-${column.type}-${column.rows.map((r) => r.ports.join(',')).join('|')}`;
        return (
          <React.Fragment key={columnKey}>
            <HardwareSlotsColumn>
              {column.group && (
                <GroupLabel>
                  <GroupLine side="left" />
                  <GroupText>{column.group}</GroupText>
                  <GroupLine side="right" />
                </GroupLabel>
              )}
              {column.rows.map((row) => {
                const rowKey = `row-${label.content}-${column.type}-${row.ports.join('-')}-${row.orientation}`;
                return renderHardwarePortRow(
                  rowKey,
                  ports,
                  row.ports,
                  row.orientation,
                  column.type,
                  row.portOrientation,
                  layout.portLabels,
                );
              })}
            </HardwareSlotsColumn>
            {column.trailingGap && columnIndex < layout.columns.length - 1 && (
              <HardwareSlotsTrailingGap style={{ width: column.trailingGap }} />
            )}
          </React.Fragment>
        );
      })}
      {layout.rearColumns && (
        <HardwareSlotsRear>
          {layout.rearColumns?.map((column, columnIndex) => {
            const columnKey = `rear-column-${label.content}-${column.type}-${column.rows.map((r) => r.ports.join(',')).join('|')}`;
            return (
              <React.Fragment key={columnKey}>
                <HardwareSlotsColumn>
                  {column.group && (
                    <GroupLabel>
                      <GroupLine side="left" />
                      <GroupText>{column.group}</GroupText>
                      <GroupLine side="right" />
                    </GroupLabel>
                  )}
                  {column.rows.map((row) => {
                    const rowKey = `row-${label.content}-${column.type}-${row.ports.join('-')}-${row.orientation}`;
                    return renderHardwarePortRow(
                      rowKey,
                      ports,
                      row.ports,
                      row.orientation,
                      column.type,
                      row.portOrientation,
                      layout.portLabels,
                    );
                  })}
                </HardwareSlotsColumn>
                {column.trailingGap && columnIndex < layout.columns.length - 1 && (
                  <HardwareSlotsTrailingGap style={{ width: column.trailingGap }} />
                )}
              </React.Fragment>
            );
          })}
        </HardwareSlotsRear>
      )}
    </HardwareSlotsRow>
  );
}
